import { useEffect, useMemo, useState } from "react"

import { useSelector } from "react-redux"

import { selectUserTimezone } from "@selectors/user"
import { UNKNOWN_TIMEZONE } from "@utils/peopleUtils"
import dayjs from "@utils/dayjs"

import { getZoneOffsetMinutes } from "./timezoneDisplay"

const REFRESH_INTERVAL_MS = 60 * 1000

// Shared zone-grouping logic (Multi-TZ Sync Up Detail Group, Notion e8f3d605c41448f98d39588e55f18c8e).
// Extracted from Content/Timezone/index.jsx so the People-card group AND the Time Zone Summary
// popup (restores the original 2018-2019 one-tap modal) render the EXACT SAME ordering/bucketing
// -- one source of truth, no drift between the two surfaces.
export const useTimezoneGroups = people => {
  const [now, setNow] = useState(() => dayjs())
  const viewerTimezone = useSelector(selectUserTimezone) || dayjs.tz.guess()

  // Spec: "Times tick live (interval refresh is fine ...)".
  useEffect(() => {
    const intervalId = setInterval(() => setNow(dayjs()), REFRESH_INTERVAL_MS)
    return () => clearInterval(intervalId)
  }, [])

  const { zoneGroups, unknownGroup } = useMemo(() => {
    const groups = {}
    const unknown = []

    ;(people || []).forEach(person => {
      const tz = person?.timezone
      if (!tz || tz === UNKNOWN_TIMEZONE) {
        unknown.push(person)
        return
      }
      groups[tz] = groups[tz] || []
      groups[tz].push(person)
    })

    const viewerOffsetMinutes = getZoneOffsetMinutes(viewerTimezone, now)

    const zoneGroups = Object.entries(groups).map(([timezone, zonePeople]) => {
      const zoneOffsetMinutes = getZoneOffsetMinutes(timezone, now)
      const offsetHours = Math.round(
        (zoneOffsetMinutes - viewerOffsetMinutes) / 60
      )
      return {
        timezone,
        people: zonePeople,
        offsetHours,
        isViewerZone: timezone === viewerTimezone,
      }
    })

    // Spec: "the viewer's own timezone FIRST, then ascending by absolute offset from the
    // viewer with positive offsets before negative at equal distance (example: +1, -1, +4, +6,
    // -7, +8)".
    zoneGroups.sort((a, b) => {
      if (a.isViewerZone !== b.isViewerZone) return a.isViewerZone ? -1 : 1

      const absDiff = Math.abs(a.offsetHours) - Math.abs(b.offsetHours)
      if (absDiff !== 0) return absDiff

      if (a.offsetHours !== b.offsetHours) return b.offsetHours - a.offsetHours

      return 0
    })

    return { zoneGroups, unknownGroup: unknown }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [people, viewerTimezone, now])

  return { zoneGroups, unknownGroup, now, viewerTimezone }
}
